import { useCallback, useEffect, useRef, useState } from "react";
import api, { API_BASE_URL, getAllRecords, openProtectedFile } from "../api";
import SearchableSelect from "../components/SearchableSelect";
import TablePagination from "../components/TablePagination";
import "../fuel-calculator.css";

const empty = {
  date: new Date().toISOString().slice(0, 10),
  vehicle: "",
  driver: "",
  odometerReading: "",
  fuelQuantity: "",
  ratePerLiter: "",
  totalAmount: "",
  paymentReferenceNumber: "",
  paymentProof: null,
};
const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-IN") : "—";
const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;
const protectedFileUrl = (filePath) => `${API_BASE_URL.replace(/\/$/, "")}${filePath}`;
const positiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};
const calculatedValue = (value, decimals) =>
  Number.isFinite(value) && value > 0
    ? String(Number(value.toFixed(decimals)))
    : "";

function FuelForm({ close, saved, entry = null }) {
  const [form, setForm] = useState(() =>
    entry
      ? {
          ...empty,
          date: entry.date
            ? new Date(entry.date).toISOString().slice(0, 10)
            : empty.date,
          vehicle: entry.vehicle || "",
          driver: entry.driver || "",
          odometerReading: entry.odometerReading ?? "",
          fuelQuantity: entry.fuelQuantity ?? "",
          ratePerLiter: entry.ratePerLiter ?? "",
          totalAmount: entry.totalAmount ?? "",
          paymentReferenceNumber:
            entry.paymentReferenceNumber || entry.remarks || "",
        }
      : empty,
  );
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // Remember the value we filled so later edits to either source value keep it in sync.
  const calculatedFuelField = useRef(null);
  useEffect(() => {
    Promise.all([getAllRecords("/vehicles"), getAllRecords("/drivers")])
      .then(([vehicleRows, driverRows]) => {
        setVehicles(vehicleRows);
        setDrivers(driverRows);
      })
      .catch((err) =>
        setError(
          err.response?.data?.message ||
            "Unable to load vehicle and driver details.",
        ),
      );
  }, []);
  const set = (key) => (event) =>
    setForm((current) => ({
      ...current,
      [key]:
        event.target.type === "file"
          ? event.target.files[0]
          : event.target.value,
    }));
  const setFuelValue = (key) => (event) => {
    const value = event.target.value;
    setForm((current) => {
      const next = { ...current, [key]: value };
      const quantity = positiveNumber(next.fuelQuantity);
      const rate = positiveNumber(next.ratePerLiter);
      const total = positiveNumber(next.totalAmount);
      const fuelFields = ["fuelQuantity", "ratePerLiter", "totalAmount"];
      const emptyField = fuelFields.filter((field) => next[field] === "");
      let fieldToCalculate = null;

      // Fill the one blank field as soon as the other two contain valid values.
      if (emptyField.length === 1 && [quantity, rate, total].filter(Boolean).length === 2) {
        fieldToCalculate = emptyField[0];
      // Once a value has been filled automatically, changing either input updates it.
      } else if (
        emptyField.length === 0 &&
        calculatedFuelField.current &&
        calculatedFuelField.current !== key &&
        [quantity, rate, total].every(Boolean)
      ) {
        fieldToCalculate = calculatedFuelField.current;
      }

      if (fieldToCalculate === "fuelQuantity") {
        next.fuelQuantity = calculatedValue(total / rate, 4);
      } else if (fieldToCalculate === "ratePerLiter") {
        next.ratePerLiter = calculatedValue(total / quantity, 4);
      } else if (fieldToCalculate === "totalAmount") {
        next.totalAmount = calculatedValue(quantity * rate, 2);
      }

      calculatedFuelField.current = fieldToCalculate;
      return next;
    });
  };
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    const vehicle = vehicles.find((item) => item._id === form.vehicle);
    const driver = drivers.find((item) => item._id === form.driver);
    if (!vehicle || !driver) {
      setError("Select both a vehicle and driver.");
      return;
    }
    if ([form.fuelQuantity, form.ratePerLiter, form.totalAmount].filter(positiveNumber).length < 2) {
      setError("Enter any two of Quantity, Rate per Litre, and Total Amount.");
      return;
    }
    setSaving(true);
    try {
      const data = new FormData();
      Object.entries({
        ...form,
        vehicleNo: vehicle.vehicleNumber,
        driverName: driver.fullName,
        odometerReading: Number(form.odometerReading),
        fuelQuantity: Number(form.fuelQuantity),
        ratePerLiter: Number(form.ratePerLiter),
        totalAmount: Number(form.totalAmount),
      }).forEach(([key, value]) => {
        if (value !== null) data.append(key, value);
      });
      if (entry) {
        await api.put(`/fuel/${entry._id}`, data);
        saved("Fuel entry updated.");
      } else {
        await api.post("/fuel", data);
        saved("Fuel entry added.");
      }
      close();
    } catch (err) {
      setError(err.response?.data?.message || "Unable to save fuel entry.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fuel-modal-backdrop">
      <form
        className="fuel-modal"
        onSubmit={submit}
        onMouseDown={(event) => event.stopPropagation()}
        noValidate
      >
        <header>
          <div>
            <p>FUEL MIS</p>
            <h3>{entry ? "Edit Fuel Entry" : "Add Fuel Entry"}</h3>
          </div>
          <button type="button" onClick={close}>
            ×
          </button>
        </header>
        <div className="fuel-form-grid">
          <label className="wide payment-proof-field">
            Date
            <input
              type="date"
              value={form.date}
              onChange={set("date")}
              required
            />
          </label>
          <label>
            Vehicle Number
            <SearchableSelect
              value={form.vehicle}
              onChange={(value) =>
                setForm((current) => ({ ...current, vehicle: value }))
              }
              options={vehicles.map((vehicle) => ({
                value: vehicle._id,
                label: vehicle.vehicleNumber,
              }))}
              placeholder="Type vehicle number"
              required
            />
          </label>
          <label>
            Driver Name
            <SearchableSelect
              value={form.driver}
              onChange={(value) =>
                setForm((current) => ({ ...current, driver: value }))
              }
              options={drivers.map((driver) => ({
                value: driver._id,
                label: driver.fullName,
              }))}
              placeholder="Type driver name"
              required
            />
          </label>
          <label>
            Odometer Reading (KM)
            <input
              type="number"
              min="0"
              value={form.odometerReading}
              onChange={set("odometerReading")}
              required
            />
          </label>
          <label>
            Fuel Quantity (Litres)
            <input
              type="number"
              min="0.01"
              step="0.0001"
              value={form.fuelQuantity}
              onChange={setFuelValue("fuelQuantity")}
            />
          </label>
          <label>
            Rate per Litre
            <input
              type="number"
              min="0.01"
              step="0.0001"
              value={form.ratePerLiter}
              onChange={setFuelValue("ratePerLiter")}
            />
          </label>
          <label>
            Total Amount
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={form.totalAmount}
              onChange={setFuelValue("totalAmount")}
            />
          </label>
          <div className="fuel-calculation-hint">
            Enter any two values and the remaining value is calculated automatically.
            All values remain editable.
          </div>
          <label>
            Payment Proof
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={set("paymentProof")}
            />
            <small>PDF, JPG, or PNG · max 5 MB</small>
          </label>
          <label className="wide">
            Transaction Reference Number
            <input
              value={form.paymentReferenceNumber}
              onChange={set("paymentReferenceNumber")}
            />
          </label>
          {error && <p className="fuel-error">{error}</p>}
        </div>
        <footer>
          <button type="button" className="cancel" onClick={close}>
            Cancel
          </button>
          <button disabled={saving}>
            {saving
              ? "Saving…"
              : entry
                ? "Update Fuel Entry"
                : "Save Fuel Entry"}
          </button>
        </footer>
      </form>
    </div>
  );
}

export default function FuelMisPage() {
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [importing, setImporting] = useState(false);
  const importInput = useRef(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/fuel", { params: { page, limit: 25, query } });
      setRecords(response.data.data || []);
      setCount(Number(response.data.count) || 0);
    } catch (error) {
      setNotice(
        error.response?.data?.message || "Unable to load fuel entries.",
      );
    } finally {
      setLoading(false);
    }
  }, [page, query]);
  useEffect(() => {
    load();
  }, [load]);
  const importFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const response = await api.post("/fuel/import", data);
      setNotice(
        `${response.data.message}${response.data.skipped ? ` ${response.data.skipped} row(s) skipped.` : ""}`,
      );
      load();
    } catch (error) {
      setNotice(
        error.response?.data?.message || "Unable to import the Excel file.",
      );
    } finally {
      setImporting(false);
      event.target.value = "";
    }
  };
  const exportExcel = () => {
    const headers = [
      "Sl",
      "Date",
      "Vehicle Number",
      "Driver Name",
      "Odometer Reading",
      "Fuel Quantity",
      "Rate Per Liter",
      "Total Amount",
      "Payment Proof",
      "Transaction Reference Number",
    ];
    const rows = records.map((record, index) => [
      (page - 1) * 25 + index + 1,
      formatDate(record.date),
      record.vehicleNo,
      record.driverName,
      record.odometerReading,
      record.fuelQuantity,
      record.ratePerLiter,
      record.totalAmount,
      record.paymentProof ? protectedFileUrl(record.paymentProof) : "",
      record.paymentReferenceNumber || record.remarks || "",
    ]);
    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `fuel-mis-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="fuel-mis">
      <div className="fuel-mis-heading">
        <div>
          <p>MIS REPORTS</p>
          <h2>Fuel MIS</h2>
          <span>Add and review all vehicle fuel entries.</span>
        </div>
        <div className="fuel-mis-actions">
          <input
            ref={importInput}
            type="file"
            accept=".xlsx,.xls"
            onChange={importFile}
            hidden
          />
          <button
            className="import"
            onClick={() => importInput.current?.click()}
            disabled={importing}
          >
            {importing ? "Importing…" : "Import Excel"}
          </button>
          <button className="import" onClick={exportExcel}>
            Export Excel
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
          >
            + Add Fuel Entry
          </button>
        </div>
      </div>
      {notice && (
        <div className="fuel-notice">
          {notice}
          <button onClick={() => setNotice("")}>×</button>
        </div>
      )}
      <section>
        <div className="fuel-tools">
          <input
            placeholder="Search vehicle, driver, or station"
            value={query}
            onChange={(event) => { setQuery(event.target.value); setPage(1); }}
          />
          <button onClick={load}>Refresh</button>
        </div>
        {loading ? (
          <p>Loading fuel entries…</p>
        ) : (
          <div className="fuel-table action-table">
            <table>
              <thead>
                <tr>
                  {[
                    "Sl",
                    "Date",
                    "Vehicle Number",
                    "Driver Name",
                    "Odometer (KM)",
                    "Quantity (L)",
                    "Rate/Litre",
                    "Total Amount",
                    "Payment Proof",
                    "Transaction Reference Number",
                    "Action",
                  ].map((column) => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((record, index) => (
                  <tr key={record._id}>
                    <td>{(page - 1) * 25 + index + 1}</td>
                    <td>{formatDate(record.date)}</td>
                    <td>{record.vehicleNo}</td>
                    <td>{record.driverName}</td>
                    <td>{record.odometerReading}</td>
                    <td>{record.fuelQuantity}</td>
                    <td>{money(record.ratePerLiter)}</td>
                    <td>{money(record.totalAmount)}</td>
                    <td>
                      {record.paymentProof ? (
                        <a
                          href={protectedFileUrl(record.paymentProof)}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            openProtectedFile(record.paymentProof).catch(() =>
                              setNotice("Unable to open the payment proof."),
                            );
                          }}
                        >
                          View proof
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {record.paymentReferenceNumber || record.remarks || "—"}
                    </td>
                    <td>
                      {(
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(record);
                            setShowForm(true);
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {!records.length && (
                  <tr>
                    <td colSpan="12">No fuel entries found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <TablePagination page={page} setPage={setPage} total={count} pageSize={25} label="Fuel entries" />
      </section>
      {showForm && (
        <FuelForm
          entry={editing}
          close={() => {
            setShowForm(false);
            setEditing(null);
          }}
          saved={(message) => {
            setNotice(message);
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
